/**
 * Payroll Review Center — Phase 5.
 *
 * Route: /payroll/runs/:id  (HR Manager / Admin / Owner only, gated
 * upstream by <RoleGate>). Loads a run, shows summary tiles + a
 * per-employee table with payslip status, actions to preview, approve,
 * send back for correction (with mandatory note), recalculate. Bulk
 * approve on the table. Once every row is Approved the run flips to
 * `approved` and the Finalize & Dispatch button lights up. Post
 * dispatch: everything is locked; a Download all PDFs and a per-row
 * download link remain available.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { fmtMoney } from "@/lib/helpers";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { devWarn } from "@/lib/devLog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  CheckCircle, XCircle, ArrowsClockwise, Users, HandCoins, TrendUp, Coins,
  Warning, PaperPlaneTilt, FilePdf, LockKey, DownloadSimple,
  CheckSquare, Eye, ArrowLeft,
} from "@phosphor-icons/react";

const STATUS_META = {
  pending_review:  { label: "Pending Review", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  needs_correction:{ label: "Needs Correction", cls: "bg-rose-100 text-rose-800 border-rose-200" },
  approved:        { label: "Approved", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  dispatched:      { label: "Dispatched", cls: "bg-blue-100 text-blue-800 border-blue-200" },
  skipped_no_email:{ label: "Skipped — no email", cls: "bg-muted text-muted-foreground border-border" },
};

function statusPill(s) {
  const m = STATUS_META[s] || { label: s || "—", cls: "bg-muted text-muted-foreground border-border" };
  return <Badge className={`text-[10px] ${m.cls}`}>{m.label}</Badge>;
}

export default function PayrollReviewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectNote, setRejectNote] = useState("");
  const [dispatchReady, setDispatchReady] = useState(true);
  const [dispatchSettingsUrl, setDispatchSettingsUrl] = useState("/settings?tab=email");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/payroll-engine/runs/${id}`);
      setRun(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // Query readiness once the run is loaded — if not ready, disable
    // Dispatch and surface the link to Email Sender settings.
    (async () => {
      try {
        const { data } = await api.get("/payroll-engine/dispatch/readiness");
        setDispatchReady(!!data.ready);
        if (data.settings_url) setDispatchSettingsUrl(data.settings_url);
      } catch (e) {
        // Non-fatal: dispatch button just stays disabled if readiness
        // check errors — user can retry from the header refresh.
        devWarn("[PayrollReviewPage] dispatch readiness failed:", e);
      }
    })();
  }, []);

  const employees = run?.employees || [];
  const totals = useMemo(() => {
    let gross = 0, ded = 0, net = 0;
    for (const e of employees) {
      gross += (Number(e.base_salary) || 0) + (Number(e?.line_totals?.deductions) || 0);
      ded   += (Number(e?.line_totals?.deductions) || 0);
      net   += Number(e.net) || 0;
    }
    return { gross: Math.round(gross * 100) / 100, ded: Math.round(ded * 100) / 100, net: Math.round(net * 100) / 100 };
  }, [employees]);

  const counts = useMemo(() => {
    const c = { pending_review: 0, needs_correction: 0, approved: 0, dispatched: 0, skipped_no_email: 0 };
    for (const e of employees) c[e.payslip_status] = (c[e.payslip_status] || 0) + 1;
    return c;
  }, [employees]);

  const isDraft = run?.status === "draft";
  const isApproved = run?.status === "approved";
  const isDispatched = run?.status === "dispatched";
  const locked = isDispatched;

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); if (msg) toast.success(msg); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const bulkApprove = () =>
    act(async () => {
      const { data } = await api.post(`/payroll-engine/runs/${id}/bulk-approve`);
      toast.success(`Approved ${data.approved} payslip${data.approved === 1 ? "" : "s"}`);
    });

  const approveOne = (eid) => act(async () => {
    await api.post(`/payroll-engine/runs/${id}/employees/${eid}/approve`, {});
  }, "Payslip approved");

  const recalcOne = (eid) => act(async () => {
    await api.post(`/payroll-engine/runs/${id}/employees/${eid}/recalculate`, {});
  }, "Recalculated — status back to Pending Review");

  const rejectSubmit = async () => {
    if (!rejectNote.trim()) return toast.error("A correction note is required");
    await act(async () => {
      await api.post(`/payroll-engine/runs/${id}/employees/${rejectFor.employee_id}/reject`, {
        note: rejectNote.trim(),
      });
      setRejectFor(null); setRejectNote("");
    }, "Sent back for correction");
  };

  const dispatchRun = () =>
    act(async () => {
      const { data } = await api.post(`/payroll-engine/runs/${id}/dispatch`);
      toast.success(`Dispatched ${data.sent} payslip${data.sent === 1 ? "" : "s"} — ${data.skipped.length} skipped, ${data.failed.length} failed`);
    });

  const downloadPayslip = (eid) => {
    const url = `${API_BASE}/payroll-engine/runs/${id}/payslip/${eid}/pdf`;
    fetch(url, { credentials: "include" })
      .then(r => r.blob())
      .then(b => {
        const u = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = u; a.download = `payslip-${eid}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(u);
      })
      .catch(() => toast.error("Download failed"));
  };
  const downloadAll = () => {
    const url = `${API_BASE}/payroll-engine/runs/${id}/payslips.zip`;
    fetch(url, { credentials: "include" })
      .then(r => r.blob())
      .then(b => {
        const u = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = u; a.download = `payslips-${run.period_start}-to-${run.period_end}.zip`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(u);
      })
      .catch(() => toast.error("Download failed"));
  };

  if (!run) return <div className="text-sm text-muted-foreground p-6">Loading payroll run…</div>;

  return (
    <div className="space-y-6" data-testid="payroll-review-page">
      <div>
        <button
          onClick={() => navigate("/payroll")}
          className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-2"
          data-testid="back-to-payroll"
        >
          <ArrowLeft size={12} weight="bold" /> Back to Payroll
        </button>
        <div className="overline mb-1">Payroll Review Center</div>
        <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-3 flex-wrap">
          {run.period_start} → {run.period_end}
          <Badge className={
            isDispatched ? "bg-blue-100 text-blue-800 border-blue-200"
            : isApproved ? "bg-emerald-100 text-emerald-800 border-emerald-200"
            : "bg-amber-100 text-amber-800 border-amber-200"
          } data-testid="run-status-badge">
            {run.status}
          </Badge>
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Review every payslip, approve or send back for correction, then finalize &amp; dispatch.
          {locked && " · Run is locked."}
        </p>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Tile icon={Users} label="Employees" value={employees.length} tone="blue" />
        <Tile icon={HandCoins} label="Total gross" value={fmtMoney(totals.gross)} tone="emerald" />
        <Tile icon={Coins} label="Total deductions" value={fmtMoney(totals.ded)} tone="rose" />
        <Tile icon={TrendUp} label="Net payroll" value={fmtMoney(totals.net)} tone="primary" />
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        {!locked && (
          <Button onClick={bulkApprove} disabled={busy || !counts.pending_review} data-testid="bulk-approve" className="gap-1.5">
            <CheckSquare size={14} /> Bulk approve pending ({counts.pending_review})
          </Button>
        )}
        {isApproved && !locked && (
          <>
            {!dispatchReady && (
              <div className="flex items-center gap-2 text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded px-2 py-1" data-testid="dispatch-blocker">
                <Warning size={12} weight="fill" /> No email sender configured.
                <button onClick={() => navigate(dispatchSettingsUrl)} className="underline">Configure now</button>
              </div>
            )}
            <Button
              onClick={dispatchRun}
              disabled={busy || !dispatchReady}
              className="gap-1.5 bg-primary"
              data-testid="finalize-dispatch"
            >
              <PaperPlaneTilt size={14} weight="fill" /> Finalize &amp; Dispatch
            </Button>
          </>
        )}
        {(isApproved || isDispatched) && (
          <Button variant="outline" onClick={downloadAll} className="gap-1.5" data-testid="download-all">
            <DownloadSimple size={14} /> Download all PDFs
          </Button>
        )}
        {locked && (
          <Badge className="bg-blue-100 text-blue-800 border-blue-200 gap-1">
            <LockKey size={11} /> Locked · Dispatched
          </Badge>
        )}
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="grid grid-cols-[110px,minmax(0,2fr),110px,100px,120px,120px,140px,140px] gap-2 px-4 py-2 bg-muted/40 text-[10px] uppercase font-semibold tracking-wide text-muted-foreground border-b border-border">
            <div>Emp #</div>
            <div>Employee</div>
            <div>Branch</div>
            <div>Model</div>
            <div className="text-right">Gross</div>
            <div className="text-right">Deductions</div>
            <div className="text-right">Net</div>
            <div>Status / Action</div>
          </div>
          {employees.length === 0 && (
            <div className="p-6 text-sm text-muted-foreground text-center">No employees in this run.</div>
          )}
          <div className="divide-y divide-border">
            {employees.map(e => {
              const gross = (Number(e.base_salary) || 0) + (Number(e?.line_totals?.deductions) || 0);
              const ded = Number(e?.line_totals?.deductions) || 0;
              const flagged = !(e.compensation_model) || (e.base_salary === 0 && !ded);
              return (
                <div
                  key={e.employee_id}
                  className={`grid grid-cols-[110px,minmax(0,2fr),110px,100px,120px,120px,140px,140px] gap-2 px-4 py-3 items-center text-sm ${flagged ? "bg-rose-50/40" : ""}`}
                  data-testid={`review-row-${e.employee_id}`}
                >
                  <div className="font-mono text-xs">{e.employee_number || <span className="text-amber-700">Unassigned</span>}</div>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{e.employee_name}</div>
                    <div className="text-[11px] text-muted-foreground truncate">Ref: {e.payslip_ref}</div>
                    {e.review_note && (
                      <div className="text-[11px] text-rose-800 mt-0.5 truncate" data-testid={`review-note-${e.employee_id}`}>
                        <Warning size={10} className="inline mr-0.5" weight="fill" /> {e.review_note}
                      </div>
                    )}
                    {flagged && (
                      <div className="text-[11px] text-rose-800 mt-0.5">
                        <Warning size={10} className="inline mr-0.5" weight="fill" /> Missing compensation data
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{e.branch_id || "—"}</div>
                  <div className="text-xs"><Badge variant="outline" className="text-[10px]">{e.compensation_model || "—"}</Badge></div>
                  <div className="text-right tabular-nums">{fmtMoney(gross)}</div>
                  <div className="text-right tabular-nums text-rose-700">{ded > 0 ? `− ${fmtMoney(ded)}` : "—"}</div>
                  <div className="text-right tabular-nums font-display font-bold">{fmtMoney(e.net || 0)}</div>
                  <div className="flex flex-col items-start gap-1">
                    {statusPill(e.payslip_status)}
                    <div className="flex gap-1 flex-wrap">
                      <button
                        onClick={() => setPreview(e)}
                        className="text-[10px] px-1.5 py-0.5 rounded bg-muted hover:bg-muted/70 inline-flex items-center gap-0.5"
                        data-testid={`preview-${e.employee_id}`}
                      >
                        <Eye size={10} /> Preview
                      </button>
                      {!locked && e.payslip_status !== "approved" && e.payslip_status !== "dispatched" && (
                        <>
                          <button
                            onClick={() => approveOne(e.employee_id)}
                            disabled={busy}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 hover:bg-emerald-200 inline-flex items-center gap-0.5"
                            data-testid={`approve-${e.employee_id}`}
                          >
                            <CheckCircle size={10} weight="fill" /> Approve
                          </button>
                          <button
                            onClick={() => { setRejectFor(e); setRejectNote(""); }}
                            disabled={busy}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 hover:bg-rose-200 inline-flex items-center gap-0.5"
                            data-testid={`reject-${e.employee_id}`}
                          >
                            <XCircle size={10} weight="fill" /> Send back
                          </button>
                        </>
                      )}
                      {!locked && e.payslip_status === "needs_correction" && (
                        <button
                          onClick={() => recalcOne(e.employee_id)}
                          disabled={busy}
                          className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 hover:bg-amber-200 inline-flex items-center gap-0.5"
                          data-testid={`recalc-${e.employee_id}`}
                        >
                          <ArrowsClockwise size={10} weight="bold" /> Recalculate
                        </button>
                      )}
                      {(e.payslip_status === "approved" || e.payslip_status === "dispatched") && (
                        <button
                          onClick={() => downloadPayslip(e.employee_id)}
                          className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary hover:bg-primary/20 inline-flex items-center gap-0.5"
                          data-testid={`download-${e.employee_id}`}
                        >
                          <FilePdf size={10} weight="duotone" /> PDF
                        </button>
                      )}
                    </div>
                    {e.payslip_status === "dispatched" && e.dispatched_at && (
                      <div className="text-[10px] text-muted-foreground">Sent {new Date(e.dispatched_at).toLocaleString()}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Preview dialog */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="bg-card max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              Payslip · {preview?.payslip_ref}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {preview?.employee_number} · {preview?.employee_name} · {run.period_start} → {run.period_end}
            </DialogDescription>
          </DialogHeader>
          {preview && <PayslipPreview emp={preview} run={run} />}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPreview(null)}>Close</Button>
            <Button variant="outline" onClick={() => downloadPayslip(preview.employee_id)} className="gap-1.5">
              <FilePdf size={13} /> Download PDF
            </Button>
            <Button variant="outline" onClick={() => window.print()} className="gap-1.5" data-testid="print-payslip">
              Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send-back dialog */}
      <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <DialogContent className="bg-card sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Send back for correction</DialogTitle>
            <DialogDescription className="text-xs">
              A note is required so the employee&apos;s reviewer knows what to fix.
              The payslip will change to <b>Needs Correction</b>.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={rejectNote}
            onChange={e => setRejectNote(e.target.value)}
            rows={4}
            placeholder="e.g. LOP days look wrong — please review the leave approval"
            data-testid="reject-note"
          />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRejectFor(null)}>Cancel</Button>
            <Button variant="destructive" onClick={rejectSubmit} disabled={busy} data-testid="reject-submit">Send back</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- helpers ----------
function Tile({ icon: Icon, label, value, tone }) {
  const cls = {
    blue: "bg-blue-50 text-blue-800 border-blue-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    rose: "bg-rose-50 text-rose-800 border-rose-200",
    primary: "bg-primary/10 text-primary border-primary/30",
  }[tone];
  return (
    <div className={`rounded-md border px-4 py-3 ${cls}`}>
      <div className="text-[10px] uppercase font-semibold tracking-wide opacity-80 flex items-center gap-1.5"><Icon size={13} weight="duotone" /> {label}</div>
      <div className="font-display text-2xl font-bold tabular-nums mt-1">{value}</div>
    </div>
  );
}

function PayslipPreview({ emp, run }) {
  const earnings = emp?.line_totals?.detail?.earnings || [];
  const deductions = emp?.line_totals?.detail?.deductions || [];
  const rules = (run.audit_trail || []).filter(a => a.employee_id === emp.employee_id);
  const bonusRules = rules.filter(r => ["bonus", "commission", "overtime", "allowance"].includes(r.type));
  const dedRules = rules.filter(r => r.type === "deduction");
  const metrics = emp.metrics_snapshot || {};
  const lop = emp.lop || {};
  const gross = (Number(emp.base_salary) || 0) + (Number(emp?.line_totals?.deductions) || 0) + bonusRules.reduce((s, r) => s + (r.amount || 0), 0);
  const totalDed = (Number(emp?.line_totals?.deductions) || 0) + dedRules.reduce((s, r) => s + (r.amount || 0), 0);

  return (
    <div className="space-y-4 text-sm">
      <div className="grid grid-cols-2 gap-3 text-xs bg-muted/30 rounded-md p-3">
        <div><span className="text-muted-foreground">Employee #</span> <b className="font-mono">{emp.employee_number || "—"}</b></div>
        <div><span className="text-muted-foreground">Name</span> <b>{emp.employee_name}</b></div>
        <div><span className="text-muted-foreground">Model</span> <b>{emp.compensation_model}</b></div>
        <div><span className="text-muted-foreground">Payslip Ref</span> <b className="font-mono">{emp.payslip_ref}</b></div>
      </div>

      <section>
        <div className="text-[10px] uppercase font-semibold text-emerald-800 mb-1">Earnings</div>
        {earnings.length === 0 && <div className="text-xs italic text-muted-foreground">No earning lines</div>}
        {earnings.map((l, i) => (
          <Row key={`earn-${l.code || l.label || i}`} label={l.label} amount={l.amount} tone="emerald" />
        ))}
        {bonusRules.map(r => (
          <Row key={r.rule_id} label={<><span>{r.rule_name}</span> <Badge variant="outline" className="text-[9px] ml-1">{r.type}</Badge></>} amount={r.amount} tone="emerald" />
        ))}
        <Row label="Gross" amount={gross} bold />
      </section>

      <section>
        <div className="text-[10px] uppercase font-semibold text-rose-800 mb-1">Deductions</div>
        {deductions.length === 0 && dedRules.length === 0 && (
          <div className="text-xs italic text-muted-foreground">No deductions</div>
        )}
        {deductions.map((l, i) => (
          <Row key={`ded-${l.code || l.label || i}`} label={l.label} amount={l.amount} tone="rose" prefix="−" />
        ))}
        {dedRules.map(r => (
          <Row key={r.rule_id} label={r.rule_name} amount={r.amount} tone="rose" prefix="−" />
        ))}
      </section>

      <section>
        <div className="text-[10px] uppercase font-semibold text-blue-800 mb-1">Attendance &amp; Leave</div>
        <div className="grid grid-cols-2 gap-1 text-xs">
          <Row label="Total working days" amount={metrics.period_working_days || 0} plain />
          <Row label="Days present" amount={metrics.attendance_days_present || 0} plain />
          <Row label="Unpaid leave days" amount={metrics.unpaid_leave_days || 0} plain />
          <Row label="Loss of Pay days" amount={lop.days || 0} plain />
        </div>
        {lop.days > 0 && (
          <div className="text-[11px] text-rose-800 mt-1">
            LOP deduction: {lop.days} × ₹{Number(lop.per_day).toFixed(2)} = ₹{Number(lop.amount).toFixed(2)}
          </div>
        )}
      </section>

      <section className="pt-3 border-t border-border">
        <Row label="Total earnings" amount={gross} bold />
        <Row label="Total deductions" amount={totalDed} bold tone="rose" prefix="−" />
        <div className="flex justify-between items-baseline pt-1 mt-1 border-t border-border">
          <div className="font-display font-bold text-base">Net Payable</div>
          <div className="font-display text-2xl font-bold tabular-nums">₹{Number(emp.net || 0).toLocaleString("en-IN")}</div>
        </div>
      </section>
    </div>
  );
}

function Row({ label, amount, tone, prefix, bold, plain }) {
  const color = tone === "rose" ? "text-rose-800" : tone === "emerald" ? "text-emerald-800" : "";
  return (
    <div className={`flex justify-between items-baseline text-xs py-0.5 ${plain ? "" : "border-b border-border/50"} ${bold ? "font-semibold" : ""}`}>
      <div className="min-w-0 truncate">{label}</div>
      <div className={`tabular-nums ${color}`}>
        {prefix || ""}{typeof amount === "number" ? (plain ? amount : `₹${amount.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`) : amount}
      </div>
    </div>
  );
}
